/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

const DrawerContainer = styled.div`
  position: fixed;
  bottom: 0px;
  left: ${props => (props.isExpanded ? '0' : 'max(calc((100% - 1200px) / 2), 20px)')};
  width: ${props => (props.isExpanded ? '100%' : '360px')};
  border-radius: ${props => (props.isExpanded ? '0' : '8px 8px 0 0')};
  z-index: 1000;
  background: #ffffff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
  transition: width 0.3s ease-out, border-radius 0.3s ease, bottom 0.3s ease-out, left 0.3s ease-out;

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 0;
    bottom: 0;
    left: 0;
  }
`;

const HeaderBar = styled.div`
  background: #000000;
  color: #ffffff;
  cursor: pointer;
  min-height: 50px;
`;

const HeaderInner = styled.div`
  display: flex;
  height: 70px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  position: relative;

  @media (max-width: 1200px) {
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const HeaderContent = styled.div`
  color: #ffffff;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: 'OCRoboto';
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 18px;
  }
`;

const ChevronContainer = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);

  @media (max-width: 1200px) {
    right: 40px;
  }

  @media (max-width: 768px) {
    right: 20px;
  }
`;

const ChevronIcon = styled.i`
  font-size: 20px;
  transition: transform 0.5s ease;
  transform: ${props => (props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DrawerContent = styled.div`
  background: #ffffff;
  max-height: ${props => (props.isExpanded ? '500px' : '0')};
  opacity: ${props => (props.isExpanded ? '1' : '0')};
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
`;

const ContentInner = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 1200px) {
    padding: 20px 40px;
  }

  @media (max-width: 768px) {
    padding: 20px 20px;
  }
`;

const BottomDrawer = ({
  isExpanded = false,
  onToggle,
  headerContent,
  children,
  className = '',
  headerClassName = '',
  ...props
}) => {
  const handleHeaderClick = e => {
    if (onToggle) {
      onToggle(e);
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleHeaderClick(event);
    }
  };

  // Add/remove body padding when drawer is present to prevent footer blocking
  useEffect(() => {
    // Keep consistent padding since drawer maintains height when collapsed
    document.body.style.paddingBottom = '230px';

    return () => {
      // Cleanup when component unmounts
      document.body.style.paddingBottom = '';
    };
  }, []);

  return (
    <DrawerContainer className={className} isExpanded={isExpanded} {...props}>
      <HeaderBar
        className={headerClassName}
        onClick={handleHeaderClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} drawer`}
      >
        <HeaderInner>
          <HeaderContent>{headerContent}</HeaderContent>
          <ChevronContainer>
            <ChevronIcon className="fa fa-chevron-down" isExpanded={isExpanded} aria-hidden="true" />
          </ChevronContainer>
        </HeaderInner>
      </HeaderBar>

      <DrawerContent isExpanded={isExpanded}>
        <ContentInner>{children}</ContentInner>
      </DrawerContent>
    </DrawerContainer>
  );
};

BottomDrawer.propTypes = {
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  headerContent: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
};

BottomDrawer.defaultProps = {
  isExpanded: false,
  onToggle: null,
  headerContent: null,
  children: null,
  className: '',
  headerClassName: '',
};

export default BottomDrawer;
